// src/app/pages/dashboard/components/stat-card/stat-card.component.ts
import { Component, Input, ChangeDetectionStrategy, OnChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { AnalysisStore } from '../../../../core/shared/analysis.store';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

export interface StatCard {
    id: 'total_population' | 'undiagnosed' | 'diagnosed' | 'treated' | 'controlled' | 'cvd_events' | 'cvd_patients' | 'cvd_deaths';
    title: string;
    percent: number;
    value: number;
    accentColor?: string;
    tooltipTitle?: string;
}

type HtnMap = Record<string, Record<string, number>>;
type StatRow = {
    year: number;
    diagnosed_total: number;
    undiagnosed_total: number;
    treated_total: number;
    controlled_total?: number;
    controlled_male: number;
    controlled_female: number;
    total_population_male?: number;
    total_population_female?: number;
};

@Component({
    selector: 'app-stats-card',
    standalone: true,
    imports: [CommonModule, TooltipModule],
    templateUrl: './stat-card.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatsCardComponent implements OnChanges {
    @Input() cards: StatCard[] | null = null;
    @Input() htn: HtnMap | null = null;
    @Input() runIndex: number | null = null;
    @Input() yearOverride: string | null = null;
    @Input() rows: Array<StatRow> | null = null;
    @Input() locale = 'en-US';

    private store = inject(AnalysisStore);

    // Selected simulation name (same logic as AnalysisComponent)
    readonly selectedSimulation$ = combineLatest([this.store.selectedRunId$, this.store.runsList$]).pipe(
        map(([id, runs]) => {
            const run = id ? runs.find((r) => r.id === id) : runs.length ? runs[runs.length - 1] : undefined;
            return run?.title ?? '-';
        })
    );

    private _computedCards: StatCard[] = [];
    get displayCards(): StatCard[] {
        return this._computedCards;
    }

    private iconMap: Record<StatCard['id'], string> = {
        total_population: '../../assets/images/statCard/smiley.svg',
        undiagnosed: '../../assets/images/statCard/location_question.svg',
        diagnosed: '../../assets/images/statCard/heart_diagnosed.svg',
        treated: '../../assets/images/statCard/heart_treated.svg',
        controlled: '../../assets/images/statCard/controlled.svg',
        cvd_events: '../../assets/images/statCard/smiley.svg',
        cvd_patients: '../../assets/images/statCard/location_question.svg',
        cvd_deaths: '../../assets/images/statCard/smiley.svg'
    };

    iconSrc(id: StatCard['id']): string {
        return this.iconMap[id] || '/assets/icons/stat-default.png';
    }

    ngOnChanges(): void {
        this._computedCards = this.buildCards();
    }

    private buildCards(): StatCard[] {
        if (this.htn) {
            const years = this.collectYears(this.htn);
            const year = this.yearOverride ?? (this.runIndex === 0 ? '2011' : years.length ? years[years.length - 1] : '2011');

            const get = (k: string) => this.htn?.[k]?.[year];
            const v = (k: string) => Number(String(get(k) ?? 0).replace(/,/g, ''));

            const totalPopulation = v('Diagnosed') + v('Undiagnosed');

            const diagnosed = v('Diagnosed') ?? 0;
            const undiagnosed = v('Undiagnosed') ?? 0;
            const treated = v('Treated') ?? 0;
            const controlled = v('Controlled') ?? 0;

            const hypertensionTotal = diagnosed + undiagnosed;
            const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0);

            const diagnosedPct = pct(diagnosed, hypertensionTotal);
            const undiagnosedPct = pct(undiagnosed, hypertensionTotal);
            const treatedPct = pct(treated, diagnosed);
            const controlledPct = pct(controlled, diagnosed);

            const tt = (title: string) => `${title}`;

            return [
                { id: 'total_population', title: 'Total HT Population', percent: 100, value: totalPopulation, tooltipTitle: tt('Total Hypertensive Population (Hypertension + CVD)') },
                { id: 'undiagnosed', title: 'Undiagnosed', percent: undiagnosedPct, value: undiagnosed, tooltipTitle: tt('% of total hypertensive population') },
                { id: 'diagnosed', title: 'Diagnosed', percent: diagnosedPct, value: diagnosed, tooltipTitle: tt('% of total hypertensive population') },
                { id: 'treated', title: 'Treated', percent: treatedPct, value: treated, tooltipTitle: tt('% of Diagnosed') },
                { id: 'controlled', title: 'Controlled', percent: controlledPct, value: controlled, tooltipTitle: tt('% of Diagnosed') }
            ];
        }

        if (this.rows?.length) {
            const isInitial = this.runIndex === 0;
            const row = isInitial ? this.rows.find((r) => r.year === 2011) || this.rows[0] : this.rows[this.rows.length - 1];
            if (row) {
                const year = String(row.year);
                const totalPopulation = (row.total_population_male ?? 0) + (row.total_population_female ?? 0);
                const diagnosed = row.diagnosed_total ?? 0;
                const undiagnosed = row.undiagnosed_total ?? 0;
                const treated = row.treated_total ?? 0;
                const controlled =
                    (row.controlled_total ?? (row.controlled_male + row.controlled_female)) ?? 0;
                const hypertensionTotal = diagnosed + undiagnosed;
                const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0);

                const diagnosedPct = pct(diagnosed, hypertensionTotal);
                const undiagnosedPct = pct(undiagnosed, hypertensionTotal);
                const treatedPct = pct(treated, hypertensionTotal);
                const controlledPct = pct(controlled, hypertensionTotal);

                const tt = (title: string, value: number, percent: number) => `${title} (${year}): ${this.formatNumber(value)} (${percent.toFixed(1)}%)`;

                return [
                    { id: 'total_population', title: 'Total HT Population', percent: 100, value: totalPopulation, tooltipTitle: tt('Total HT Population', totalPopulation, 100) },
                    { id: 'undiagnosed', title: 'Undiagnosed', percent: undiagnosedPct, value: undiagnosed, tooltipTitle: tt('Undiagnosed', undiagnosed, undiagnosedPct) },
                    { id: 'diagnosed', title: 'Diagnosed', percent: diagnosedPct, value: diagnosed, tooltipTitle: tt('Diagnosed', diagnosed, diagnosedPct) },
                    { id: 'treated', title: 'Treated', percent: treatedPct, value: treated, tooltipTitle: tt('Treated', treated, treatedPct) },
                    { id: 'controlled', title: 'Controlled', percent: controlledPct, value: controlled, tooltipTitle: tt('Controlled', controlled, controlledPct) }
                ];
            }
        }

        return [];
    }

    private collectYears(htn: HtnMap): string[] {
        const set = new Set<string>();
        Object.values(htn).forEach((m) => Object.keys(m || {}).forEach((y) => set.add(y)));
        return Array.from(set).sort((a, b) => Number(a) - Number(b));
    }

    trackById = (_: number, c: StatCard) => c.id;

    formatNumber(n: number | null | undefined): string {
        if (n === null || n === undefined) return '';
        const rounded = Math.ceil(n);
        return new Intl.NumberFormat(this.locale).format(rounded);
    }

    formatMillions(n: number | null | undefined): string {
        if (n === null || n === undefined) return '';
        const val = Math.ceil(n);
        const millions = val / 1_000_000;
        return `${millions.toFixed(2)}M`;
    }
}
